import * as R from 'ramda'
import moment from 'moment'
import * as CommercialTermsValidation from '../validation/commercial-terms-validation'
import {
  Building,
  EquipmentCategory,
  Equipment,
  Offer,
  CommercialTerms,
  Opportunity,
  ClientProfile,
  EquipmentGroupStId,
  EquipmentGroups,
  ClientFrontlineConfig,
  PricingModel,
  PricingLevelOptions,
  PricingScope,
  PricingOptions,
  Elevator,
  BundleGroupSelection,
  BundleGroup,
  Bundle
} from '../../../types/types'
import { CommercialTermRow } from '../../../types/tables'
import { equipmentCategories } from '../utils/utils'
import { CommercialTermKey } from 'nemo-pricing'
import { filterEquipmentBy, getFlattenedItems } from '../../common/common-utils'
import { dcsServicesForGrpTag, dxKoneConnectivityDeviceValue } from '../../common/equipment-utils'
import { isOfferUnsaved } from 'app/app/common/offer-state-predicates'
import { hasContracts } from 'app/app/common/opportunity'
import { mergeObjects } from 'app/app/common/ramda-utils'

// ------------------------------------
// Lenses
// ------------------------------------

export const getElevatorCountForOffer = (buildings: Building[]) =>
  buildings.map(getBuildingElevatorCount).reduce(R.add, 0)

export const getEscalatorCountForOffer = (buildings: Building[]) =>
  buildings.map(getBuildingEscalatorCount).reduce(R.add, 0)

export const getDoorCountForOffer = (buildings: Building[]) => buildings.map(getBuildingDoorCount).reduce(R.add, 0)

export const getPeopleCountForOffer = (buildings: Building[]) =>
  buildings.map(getBuildingResiflowCount).reduce(R.add, 0)

export const getBuildingCountForOffer = (buildings: Building[]) => R.length(buildings) || 0

export const getBuildingEquipmentCount = (building: Building) => R.pathOr([], ['details', 'equipment'], building).length

const getInteractionEquipmentCount = (building: Building) =>
  getBuildingEquipmentCount(building) - getBuildingResiflowCount(building)

export const getEquipmentCountForOffer = (buildings: Building[]) =>
  buildings.map(getInteractionEquipmentCount).reduce(R.add, 0)

const getBuildingElevatorCount = (building: Building) => getEquipmentCountByEquipmentCategory(building, 'elevator')

const getBuildingEscalatorCount = (building: Building) => getEquipmentCountByEquipmentCategory(building, 'escalator')

const getBuildingDoorCount = (building: Building) => getEquipmentCountByEquipmentCategory(building, 'door')

const getBuildingResiflowCount = (building: Building) => getEquipmentCountByEquipmentCategory(building, 'resiflow')

const getEquipmentCountByEquipmentCategory = (building: Building, equipmentCategory: EquipmentCategory) => {
  const equipment = R.pathOr([], ['details', 'equipment'], building)
  return filterEquipmentBy(equipmentCategory)(equipment).length
}

export const vasAddendumHasNoEscalators = (offer: Offer): boolean => getEscalatorCountForOffer(offer.buildings) === 0

export const vasAddendumHasNoResiflow = (offer: Offer): boolean => getPeopleCountForOffer(offer.buildings) === 0

export const unsavedOffersCount = (offers: Offer[]) => R.pipe(R.filter(isOfferUnsaved), R.length)(offers)

export function setOfferEdited(offer: Offer) {
  const isOfferFinalizing =
    offer.finalization_started || offer.finalized || Object.keys(offer.finalizationProgress || {}).length > 0
      ? true
      : false
  return mergeObjects(offer, {
    modified_client: moment.utc().locale('en').format(),
    completed: isOfferFinalizing
  })
}

export const getCommercialTermsErrors = ({
  commercialTermsConfig,
  selectedCommercialTerms,
  isContractStartDateAtEquipmentLevel,
  isBillingModeApplied,
  isBillingPlanAtEquipmentLevel,
  isOrderReasonAtEquipmentLevel
}: {
  commercialTermsConfig: CommercialTermRow[]
  selectedCommercialTerms: CommercialTerms
  isContractStartDateAtEquipmentLevel: boolean
  isBillingModeApplied: boolean
  isBillingPlanAtEquipmentLevel: boolean
  isOrderReasonAtEquipmentLevel: boolean
}): PRecord<CommercialTermKey, never[]> => {
  // Guard against undefined commercial terms
  if (!selectedCommercialTerms || !commercialTermsConfig) {
    return {}
  }

  return commercialTermsConfig.reduce(
    (acc, commercialTerm) =>
      CommercialTermsValidation.validateCommercialTerm(
        selectedCommercialTerms,
        commercialTerm,
        isContractStartDateAtEquipmentLevel,
        isBillingModeApplied,
        isBillingPlanAtEquipmentLevel,
        isOrderReasonAtEquipmentLevel
      )
        ? acc
        : R.assoc(commercialTerm.key, [], acc), // An empty list for now. Figure out the exact API later on.
    {}
  )
}

const isRenegotiation = (opportunity: Opportunity) => hasContracts(opportunity)

export const shouldUseRenegotiationPricing = (
  opportunity: Opportunity,
  profile: ClientProfile,
  pricingModel: PricingModel
) =>
  profile.canUseRenegotiationPricing &&
  isRenegotiation(opportunity) &&
  (pricingModel === 'optionA' || pricingModel === 'optionAC')

export const isQuickTender = (offer: Offer) => R.not(isFlexible(offer))

export const isFlexible = (offer: Offer) => offer.quickTenderScope === 'flexible'

export const offerHasSapContract = (offer: Offer | undefined) => (offer ? R.has('sapContractNumber', offer) : false)

/* copies all selection values
 * from:
 * offer.equipmentGroups.{equipmentCategory}.selections
 * to:
 * offer.initialData.{equipmentCategory}.selections */
export const setInitialBuildingSelections = (offer: Offer) => {
  const getGroupSelectionsFor = (lensPath: R.Path, group: EquipmentGroupStId) => {
    const lens = R.lensPath(lensPath)
    return R.view(lens, group)
  }

  const copySelections = (lensPath: R.Path) => (group: EquipmentGroupStId) => {
    // selections are available under 'selections' key
    const groupSelections = getGroupSelectionsFor(lensPath.concat('selections'), group)
    if (groupSelections) {
      return R.assocPath(lensPath, groupSelections, group)
    } else {
      return R.dissocPath(lensPath, group)
    }
  }

  const selections = R.mapObjIndexed(
    (group, _) =>
      R.pipe(
        R.pick<EquipmentGroups, EquipmentGroupStId>(equipmentCategories) as any, // Discard all other fields
        copySelections(['elevator', 'selections']),
        copySelections(['escalator', 'selections'])
      )(group),
    offer.equipmentGroups
  )

  return R.assocPath(['initialData', 'selections'], selections, offer)
}

const setInitialInteractionSelections = (overwrite: boolean) => (offer: Offer) => {
  const initialInteraction = R.path(['initialData', 'interaction'], offer)
  const updatedInitialInteraction = overwrite ? offer.interaction : initialInteraction || offer.interaction
  return R.assocPath(['initialData', 'interaction'], updatedInitialInteraction, offer)
}

const setInitialPrices = (overwrite: boolean) => (offer: Offer) => {
  const initialPrices = (offer.initialData ? offer.initialData.prices : undefined) || {}
  const updatedInitialPrices = offer.prices.reduce((acc, price) => {
    const { equipmentId } = price
    const initialPrice = overwrite
      ? price.discountedPrice
      : R.propOr(price.equipmentPrice || 0, equipmentId, initialPrices)
    return R.assoc(equipmentId, initialPrice, acc)
  }, {})
  return R.assocPath(['initialData', 'prices'], updatedInitialPrices, offer)
}

export const setInitialWarrantyOptions = (frontlineConfig: ClientFrontlineConfig) => (offer: Offer) => {
  const { warrantyOptions } = frontlineConfig
  if (warrantyOptions == null || warrantyOptions.length === 0) {
    return R.dissoc('warranty', offer)
  }
  const { warranty } = offer
  if (warranty != null) {
    return offer
  }
  return R.assoc('warranty', 0, offer)
}

export const setInitialData = (overwrite: boolean, frontlineConfig: ClientFrontlineConfig) => (offer: Offer) =>
  R.pipe(
    setInitialBuildingSelections,
    setInitialInteractionSelections(overwrite),
    setInitialPrices(overwrite),
    setInitialWarrantyOptions(frontlineConfig)
  )(offer)

export const getEquipmentForGroup = (groupId: EquipmentGroupStId, buildings: Building[] | null) =>
  R.pipe(
    R.chain((b: Building) => R.pathOr([], ['details', 'equipment'], b) as Equipment[]),
    R.filter((e: Equipment) => e.groupId === groupId)
  )(buildings || [])

export const getEquipmentByGroup = (buildings: Building[]) =>
  R.groupBy(
    equipment => equipment?.groupId,
    getFlattenedItems(R.map(building => building?.details?.equipment, buildings))
  )

export const isMarketSegmentMissing = (opportunity: Opportunity) => !opportunity.marketSegment

export const isFundingSectorMissing = (opportunity: Opportunity) => !opportunity.fundingSector

export const getInteractionVarsForOffer = (
  pricingOptions: ReadonlyArray<string>,
  interactionVars: any,
  equipmentCountForOffer: number
) => {
  const getVarForOption = (o: string) => {
    const interactionVarForOption = interactionVars[o] as [
      string,
      { mv_type: 'amount' | 'percentage'; value: number } | number
    ][]
    return interactionVarForOption.map(([interactionOption, priceEffect]) => [
      interactionOption,
      typeof priceEffect === 'number'
        ? priceEffect / equipmentCountForOffer
        : {
            ...priceEffect,
            value: priceEffect.value / equipmentCountForOffer
          }
    ])
  }
  return pricingOptions.map(o => ({ [o]: getVarForOption(o) })).reduce((acc, curVal) => ({ ...acc, ...curVal }), {})
}

// export const updatePricingConfig =
//   (pricingConfig: PricingConfig) =>
//   (frontlineConfig: ClientFrontlineConfig): ClientFrontlineConfig => ({
//     ...frontlineConfig,
//     pricingConfig
//   })

// export const getPricingVariablesForOffer = (offerSelections: OfferSelections, config: PricingConfigJson) => {
//   // const offeringQuestions = compileOfferingQuestions(offerSelections)
//   // return preparePricingConfig(offeringQuestions, config, true)
// }

export const contractInteractionOptions = <T extends string>(
  priceLevelOption: PricingLevelOptions<T>
): ReadonlyArray<string> =>
  (Object.values(priceLevelOption) as Record<'contract' | 'equipment', ReadonlyArray<string>>[])
    .map(v => v.contract)
    .reduce((acc, curVal) => acc.concat(curVal), [])

export function optionsForOfferScope(scope: PricingScope | null, options: PricingOptions) {
  switch (scope) {
    case 'flexible':
    case 'kone_care_taker':
      return options.interaction[scope]
    case 'konexion':
      return options.additional_services.konexion
    case 'value_added_services_addendum':
      return options.value_added_services_addendum
    case 'gsm_campaign': // TODO: Remove as part of GSM Clean up
      return options.gsm_campaign
    default:
      return options.interaction.flexible
  }
}

export const equipmentIsElevator = (e: Equipment): boolean => e.equipmentCategory === 'elevator'
export const isDxElevator = (e: Elevator) =>
  (e.koneConnectivityDeviceType != null &&
    e.koneConnectivityDeviceType.length &&
    e.koneConnectivityDeviceType.toLowerCase() !== dxKoneConnectivityDeviceValue) ||
  (e.apfComponent != null && e.apfComponent.length !== 0)
export const isDXEquipment = (e: Elevator) =>
  e.koneConnectivityDeviceType && e.koneConnectivityDeviceType.toLowerCase().includes(dxKoneConnectivityDeviceValue)
    ? true
    : false
export const isDCSEquipment = (e: Elevator) =>
  e.apfComponent && e.apfComponent.split(',').some(c => dcsServicesForGrpTag.includes(c.toLowerCase()))

export const anyDxElevators = (buildings: Building[]) => {
  const equipments = buildings.map(b => b.details.equipment)
  return equipments
    .flat()
    .filter(equipmentIsElevator)
    .some(equipment => isDxElevator(equipment as Elevator))
}
export const dissocBundleGroupSelection = (offer: Offer, path: string[][] | null) =>
  path
    ? path.reduce((acc, currentPath) => R.dissocPath(['bundleGroupSelection', ...currentPath], acc), offer)
    : R.assocPath(['bundleGroupSelection'], {}, offer)

export const getSelectedBundle = (
  offerBundleGroupSelection: BundleGroupSelection | undefined,
  groupId: EquipmentGroupStId,
  category: string
): string | null => {
  const selectedKeys = Object.keys(R.pathOr({}, [groupId, category], offerBundleGroupSelection))
  return selectedKeys[0] || null
}

export const hasNoPackagesInElevator = (offer: Offer) =>
  !offer.bundleGroupSelection ||
  R.isEmpty(offer.bundleGroupSelection) ||
  R.values(offer.bundleGroupSelection).every(groupData => R.isEmpty(R.pathOr({}, ['elevator'], groupData)))

export const bundleHasCoreService = (eqType: EquipmentCategory, groupBundles: BundleGroup | undefined) =>
  R.values(R.pathOr({}, [eqType], groupBundles)).some(
    (bundle: Bundle) => bundle.type === 'core_vas' || bundle.type === 'core'
  )
